import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';
import apiWithFallback from '../utils/apiWithFallback';

const DocumentTypesContext = createContext();

export const useDocumentTypes = () => {
  const context = useContext(DocumentTypesContext);
  if (!context) {
    throw new Error('useDocumentTypes must be used within a DocumentTypesProvider');
  }
  return context;
};

export const DocumentTypesProvider = ({ children }) => {
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Standard document types as fallback
  const standardDocumentTypes = [
    { id: 'invoice', name: 'Facture de vente', category: 'sales', code: 'FAC' },
    { id: 'delivery-note', name: 'Bon de livraison', category: 'sales', code: 'BL' },
    // { id: 'quote', name: 'Devis', category: 'sales', code: 'DEV' },
    { id: 'purchase-order', name: 'Bon de commande', category: 'purchase', code: 'BC' },
    { id: 'reception-note', name: 'Bon de réception', category: 'purchase', code: 'BR' }
  ];

  // Get document types by category
  const getDocumentTypesByCategory = (category) => {
    const categoryTypes = documentTypes.filter(type => type.category === category);
    if (category === 'purchase') {
      const validPurchaseTypes = ['purchase-order', 'reception-note'];
      return categoryTypes.filter(type => validPurchaseTypes.includes(type.id));
    }
    if (category === 'sales') {
      const validSalesTypes = ['invoice', 'delivery-note'];
      return categoryTypes.filter(type => validSalesTypes.includes(type.id));
    }
    return categoryTypes;
  };

  // Get document type by ID
  const getDocumentTypeById = (id) => {
    return documentTypes.find(type => type.id === id);
  };

  // Load document types
  useEffect(() => {
    const loadDocumentTypes = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Try to get document types from API with localStorage fallback
        const types = await apiWithFallback.getData(
          'documentTypes',
          apiService.getDocumentTypes,
          'stockpro_documentTypes'
        );
        
        // If we got types, use them; otherwise use standard types
        if (types && types.length > 0) {
          setDocumentTypes(types);
        } else {
          console.log('Using standard document types as fallback');
          setDocumentTypes(standardDocumentTypes);
          localStorage.setItem('stockpro_documentTypes', JSON.stringify(standardDocumentTypes));
        }
      } catch (err) {
        console.error('Error loading document types:', err);
        setError('Failed to load document types');
        
        // Use standard types as fallback
        setDocumentTypes(standardDocumentTypes);
        localStorage.setItem('stockpro_documentTypes', JSON.stringify(standardDocumentTypes));
      } finally {
        setLoading(false);
      }
    };

    loadDocumentTypes();
  }, []);

  return (
    <DocumentTypesContext.Provider
      value={{
        documentTypes,
        loading,
        error,
        getDocumentTypesByCategory,
        getDocumentTypeById
      }}
    >
      {children}
    </DocumentTypesContext.Provider>
  );
};